import type { DoorsaleEvent, EventTicketStub } from '@/types';

/**
 * Represents the raw JSON structure of a doorsale event received from the backend.
 * Booleans may arrive as 0/1 from Perl.
 */
export interface DoorsaleEventDTO {
	id: number | string;
	info1: string;
	info2: string | null;
	photo: string | null;
	price: number;
	currency: string;
	language: string;
	languages: string[];
	eventtime: number;
	eventtime_gmt_offset: number;
	eventtime_override: string | null;
	salestart: number;
	salestart_gmt_offset: number;
	saleend: number;
	saleend_gmt_offset: number;
	tz_name: string;
	venue: number;
	categories: number[];
	special_categories: number[];
	soldout: 0 | 1 | boolean;
	few_tickets?: 0 | 1 | boolean;
	status_message_text: string;
	status_message_visible: 0 | 1 | boolean;
	localizations: DoorsaleEvent['localizations'];
	doorsale_note: string;
	doorsale_customerinfo: 0 | 1 | boolean;
	price_override_active: 0 | 1 | boolean;
	price_override_text: string;
	sso_prices?: DoorsaleEvent['sso_prices'];
	tickets: EventTicketStub[];
}

/**
 * Transforms a DoorsaleEventDTO into a DoorsaleEvent, normalising 0/1 to booleans.
 */
export function mapDtoToDoorsaleEvent(dto: DoorsaleEventDTO): DoorsaleEvent {
	return {
		id: dto.id,
		info1: dto.info1 ?? '',
		info2: dto.info2 ?? '',
		photo: dto.photo ?? null,
		price: dto.price ?? 0,
		currency: dto.currency ?? '',
		language: dto.language ?? '',
		languages: Array.isArray(dto.languages) ? dto.languages : [],
		eventtime: dto.eventtime,
		eventtime_gmt_offset: dto.eventtime_gmt_offset,
		eventtime_override: dto.eventtime_override ?? null,
		salestart: dto.salestart ?? 0,
		salestart_gmt_offset: dto.salestart_gmt_offset ?? 0,
		saleend: dto.saleend ?? 0,
		saleend_gmt_offset: dto.saleend_gmt_offset ?? 0,
		tz_name: dto.tz_name ?? 'Europe/Copenhagen',
		venue: dto.venue ?? 0,
		categories: Array.isArray(dto.categories) ? dto.categories : [],
		special_categories: Array.isArray(dto.special_categories) ? dto.special_categories : [],
		soldout: Boolean(dto.soldout),
		few_tickets: dto.few_tickets !== undefined ? Boolean(dto.few_tickets) : undefined,
		status_message_text: dto.status_message_text ?? '',
		status_message_visible: Boolean(dto.status_message_visible),
		localizations: dto.localizations ?? {},
		doorsale_note: dto.doorsale_note ?? '',
		doorsale_customerinfo: Boolean(dto.doorsale_customerinfo),
		price_override_active: Boolean(dto.price_override_active),
		price_override_text: dto.price_override_text ?? '',
		sso_prices: dto.sso_prices ?? {},
		tickets: Array.isArray(dto.tickets) ? dto.tickets : [],
	};
}

/**
 * Transforms an array of DoorsaleEventDTOs into DoorsaleEvent objects.
 */
export function mapDtosToDoorsaleEvents(dtos: DoorsaleEventDTO[]): DoorsaleEvent[] {
	return dtos.map(mapDtoToDoorsaleEvent);
}
